/**
 * TransmittedContent
 *
 * Premium display for successfully transmitted packages.
 * Shows delivery confirmation with channel details, recipient, and timing.
 * Inspired by: Robinhood confirmations, Stripe receipts, Airbnb booking confirms.
 */

import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { cn } from '@/lib/utils';
import type {
  CorrespondenceChannel,
  CustomUserSchema,
} from '@/services/api/correspondenceService';

import { CHANNEL_CONFIG } from '../constants';

// ============================================================================
// Types
// ============================================================================

export interface TransmittedContentProps {
  channel: CorrespondenceChannel;
  recipientName: string;
  destinationDisplay: string;
  transmittedAt: string;
  transmittedBy?: CustomUserSchema | null;
  className?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function formatDate(timestamp: string): { date: string; time: string } {
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function getUserDisplayName(user?: CustomUserSchema | null): string {
  if (!user) return 'System';
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || user.email || 'System';
}

// ============================================================================
// Sub-components
// ============================================================================

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <div className="flex justify-between items-center py-2.5 border-b border-zinc-100 last:border-0">
    <span className="text-[12px] text-zinc-500">{label}</span>
    <span className="text-[12px] font-medium text-zinc-800 text-right max-w-[60%] truncate">
      {value}
    </span>
  </div>
);

// ============================================================================
// Component
// ============================================================================

export const TransmittedContent: React.FC<TransmittedContentProps> = ({
  channel,
  recipientName,
  destinationDisplay,
  transmittedAt,
  transmittedBy,
  className,
}) => {
  const config = CHANNEL_CONFIG[channel];
  const Icon = config.icon;
  const { date, time } = formatDate(transmittedAt);
  const sentByName = getUserDisplayName(transmittedBy);

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Success Header */}
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 mb-3">
          <CheckCircleIcon className="w-8 h-8 text-emerald-500" />
        </div>
        <h3 className="text-[15px] font-semibold text-zinc-900 mb-1">
          {config.verb} Successfully
        </h3>
        <p className="text-[12px] text-zinc-500">
          {date} at {time}
        </p>
      </div>

      {/* Details Card */}
      <div className="bg-zinc-50/60 rounded-xl px-4 py-1">
        <DetailRow
          label="Sent via"
          value={
            <span className="inline-flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-zinc-600" />
              {config.label}
            </span>
          }
        />
        <DetailRow label="Recipient" value={recipientName} />
        <DetailRow label="Destination" value={destinationDisplay} />
        <DetailRow label="Sent by" value={sentByName} />
      </div>
    </div>
  );
};
